import { PostContentType } from '@courses/types-layer/types/post'

import type { GeneralSettings } from '../types/learning-experience.types'

/** Min watched-% to auto-complete a native video on nav-away. */
export const MIN_VIDEO_PERCENT_TO_AUTO_COMPLETE = 50

/** Creator preference; defaults to `true` for legacy courses. */
export function shouldAutoCompleteLessons(settings?: GeneralSettings | null): boolean {
  return settings?.autoCompleteOn50Percent ?? true
}

/** Nav-triggered eligibility: text → always; native video → ≥50%; others handled by their own triggers. */
export function shouldAutoCompleteLessonOnNavigation(args: {
  contentType: PostContentType | string | null | undefined
  isNativeVideo: boolean
  videoProgressPercent: number
  settings?: GeneralSettings | null
}): boolean {
  if (!shouldAutoCompleteLessons(args.settings)) return false

  if (args.contentType === PostContentType.text) {
    return true
  }

  if (args.contentType === PostContentType.video && args.isNativeVideo) {
    if (!Number.isFinite(args.videoProgressPercent)) return false
    return args.videoProgressPercent >= MIN_VIDEO_PERCENT_TO_AUTO_COMPLETE
  }

  return false
}

/** Promotes a video/audio lesson to complete on media `ended` when the preference is on and not already complete. */
export function shouldAutoCompleteLessonOnMediaEnd(
  state: { ended: boolean; isCompleted: boolean; disablePostCompletionButton?: boolean },
  settings?: GeneralSettings | null
): boolean {
  if (!state.ended) return false
  if (state.isCompleted) return false
  if (state.disablePostCompletionButton) return false
  return shouldAutoCompleteLessons(settings)
}

/** Clamps cached % to <100 when preference is off so stale values can't bypass gates. */
export function normalizeAutoCompleteProgressPercentage(percentage: number, settings?: GeneralSettings | null): number {
  if (!Number.isFinite(percentage)) return 0
  if (shouldAutoCompleteLessons(settings)) return percentage
  return Math.min(percentage, 99)
}
